import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { useSubscription } from "@/hooks/useSubscription";
import { getStripeEnvironment } from "@/lib/stripe";
import { format } from "date-fns";
import {
  User as UserIcon,
  Shield,
  Camera,
  Mail,
  Calendar,
  Crown,
  KeyRound,
  LogIn,
  CheckCircle2,
  Eye,
  EyeOff,
  AlertCircle,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";

type Membership = "Free" | "Premium Monthly" | "Premium Lifetime";

const Account = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<{ full_name: string; username: string; avatar_url: string | null }>({
    full_name: "",
    username: "",
    avatar_url: null,
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [membership, setMembership] = useState<Membership>("Free");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwdError, setPwdError] = useState<string | null>(null);
  const [pwdSuccess, setPwdSuccess] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { subscription, isActive } = useSubscription(user);

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/my-learning");
        return;
      }
      setUser(user);
      const { data } = await supabase
        .from("profiles" as any)
        .select("full_name, username, avatar_url")
        .eq("id", user.id)
        .maybeSingle();
      const p = (data as any) || {};
      setProfile({
        full_name: p.full_name ?? user.user_metadata?.full_name ?? user.user_metadata?.name ?? "",
        username: p.username ?? "",
        avatar_url: p.avatar_url ?? user.user_metadata?.avatar_url ?? null,
      });

      // Determine membership tier
      const { data: lifetime } = await supabase
        .from("purchases")
        .select("id")
        .eq("user_id", user.id)
        .eq("purchase_type", "lifetime")
        .eq("environment", getStripeEnvironment())
        .limit(1);
      if (lifetime && lifetime.length > 0) setMembership("Premium Lifetime");
      setLoading(false);
    })();
  }, [navigate]);

  useEffect(() => {
    if (membership === "Premium Lifetime") return;
    setMembership(isActive ? "Premium Monthly" : "Free");
  }, [isActive, membership]);

  const provider = (user?.app_metadata as any)?.provider as string | undefined;
  const isGoogle = provider === "google";
  const isEmailUser = !provider || provider === "email";

  const initials = (profile.full_name || user?.email || "?")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Image too large", description: "Max 5 MB.", variant: "destructive" });
      return;
    }
    setUploadingAvatar(true);
    try {
      const ext = file.name.split(".").pop() || "png";
      const path = `${user.id}/avatar-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from("avatars").upload(path, file, {
        cacheControl: "3600",
        upsert: true,
      });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from("avatars").getPublicUrl(path);
      const url = pub.publicUrl;
      const { error: dbErr } = await supabase
        .from("profiles" as any)
        .update({ avatar_url: url })
        .eq("id", user.id);
      if (dbErr) throw dbErr;
      setProfile((p) => ({ ...p, avatar_url: url }));
      toast({ title: "Profile photo updated" });
    } catch (err: any) {
      toast({ title: "Upload failed", description: err.message, variant: "destructive" });
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    setSavingProfile(true);
    try {
      const { error } = await supabase
        .from("profiles" as any)
        .update({
          full_name: profile.full_name.trim() || null,
          username: profile.username.trim() || null,
        })
        .eq("id", user.id);
      if (error) throw error;
      toast({ title: "Profile saved" });
    } catch (err: any) {
      toast({
        title: "Save failed",
        description: err.message?.includes("duplicate") ? "Username already taken." : err.message,
        variant: "destructive",
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const passwordChecks = {
    length: newPassword.length >= 8,
    upper: /[A-Z]/.test(newPassword),
    lower: /[a-z]/.test(newPassword),
    number: /\d/.test(newPassword),
    symbol: /[^A-Za-z0-9]/.test(newPassword),
  };
  const strengthScore = Object.values(passwordChecks).filter(Boolean).length;
  const strengthLabel =
    strengthScore <= 2 ? "Weak" : strengthScore === 3 ? "Fair" : strengthScore === 4 ? "Good" : "Strong";
  const strengthColor =
    strengthScore <= 2 ? "bg-destructive" : strengthScore === 3 ? "bg-amber-500" : strengthScore === 4 ? "bg-blue-500" : "bg-emerald-500";

  const handleChangePassword = async () => {
    setPwdError(null);
    setPwdSuccess(false);

    if (!user?.email) {
      setPwdError("No email on this account.");
      return;
    }
    if (!currentPassword) {
      setPwdError("Enter your current password.");
      return;
    }
    if (!passwordChecks.length) {
      setPwdError("Password must be at least 8 characters.");
      return;
    }
    if (strengthScore < 3) {
      setPwdError("Password is too weak. Mix uppercase, lowercase, numbers, and symbols.");
      return;
    }
    if (newPassword === currentPassword) {
      setPwdError("New password must be different from your current password.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwdError("Passwords don't match.");
      return;
    }

    setSavingPassword(true);
    try {
      // Re-authenticate to verify current password
      const { error: signInErr } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });
      if (signInErr) {
        setPwdError("Current password is incorrect.");
        return;
      }

      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) {
        const msg = /same.*password|should be different/i.test(error.message)
          ? "New password must be different from your current password."
          : /weak|pwned|leaked/i.test(error.message)
          ? "This password has appeared in a data breach. Please choose another."
          : error.message;
        setPwdError(msg);
        return;
      }

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPwdSuccess(true);
      toast({ title: "Password updated", description: "Use your new password next time you sign in." });
    } catch (err: any) {
      setPwdError(err?.message ?? "Something went wrong. Try again.");
    } finally {
      setSavingPassword(false);
    }
  };

  const handleSendReset = async () => {
    if (!user?.email) return;
    setSendingReset(true);
    const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
      redirectTo: `${window.location.origin}/my-learning`,
    });
    setSendingReset(false);
    if (error) {
      toast({ title: "Could not send", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Reset email sent", description: "Check your inbox." });
  };

  if (loading || !user) {
    return (
      <>
        <Navigation />
        <div className="container mx-auto px-4 py-12 space-y-6">
          <Skeleton className="h-12 w-64" />
          <Skeleton className="h-64 w-full" />
        </div>
        <Footer />
      </>
    );
  }

  const createdAt = user.created_at ? format(new Date(user.created_at), "PPP") : "—";
  const lastSignIn = user.last_sign_in_at
    ? format(new Date(user.last_sign_in_at), "PPP 'at' p")
    : "—";

  return (
    <>
      <Helmet>
        <title>My Account | RunoBlog</title>
        <meta name="description" content="Manage your profile, login details, and account security." />
      </Helmet>
      <Navigation />
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-10 max-w-5xl">
          {/* Header */}
          <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold flex items-center gap-3">
                <UserIcon className="h-8 w-8 text-primary" />
                My Account
              </h1>
              <p className="text-muted-foreground mt-1">Manage your profile and security settings.</p>
            </div>
            <Button variant="outline" onClick={() => navigate("/my-learning")}>
              ← Back to Dashboard
            </Button>
          </div>

          {/* Hero card */}
          <Card className="mb-8 overflow-hidden border-primary/20">
            <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-6 md:p-8">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
                <div className="relative">
                  <Avatar className="h-24 w-24 md:h-28 md:w-28 ring-4 ring-background shadow-lg">
                    <AvatarImage src={profile.avatar_url || undefined} alt={profile.full_name || user.email || ""} />
                    <AvatarFallback className="text-2xl bg-primary/10 text-primary">{initials}</AvatarFallback>
                  </Avatar>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingAvatar}
                    className="absolute bottom-0 right-0 h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-md hover:opacity-90 disabled:opacity-50"
                    aria-label="Change photo"
                  >
                    <Camera className="h-4 w-4" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarUpload}
                  />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h2 className="text-2xl font-semibold">
                    {profile.full_name || user.email?.split("@")[0]}
                  </h2>
                  <p className="text-muted-foreground flex items-center gap-1.5 justify-center sm:justify-start mt-1">
                    <Mail className="h-4 w-4" />
                    {user.email}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3 justify-center sm:justify-start">
                    <Badge
                      variant={membership === "Free" ? "secondary" : "default"}
                      className={membership !== "Free" ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0" : ""}
                    >
                      <Crown className="h-3 w-3 mr-1" />
                      {membership}
                    </Badge>
                    {isGoogle && (
                      <Badge variant="outline" className="gap-1">
                        <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                        Google
                      </Badge>
                    )}
                    <Badge variant="outline" className="gap-1">
                      <Calendar className="h-3 w-3" />
                      Joined {createdAt}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <Tabs defaultValue="profile" className="space-y-6">
            <TabsList className="grid w-full sm:w-auto sm:inline-grid grid-cols-2">
              <TabsTrigger value="profile" className="gap-2">
                <UserIcon className="h-4 w-4" /> Profile
              </TabsTrigger>
              <TabsTrigger value="security" className="gap-2">
                <Shield className="h-4 w-4" /> Login & Security
              </TabsTrigger>
            </TabsList>

            {/* Profile */}
            <TabsContent value="profile">
              <Card>
                <CardHeader>
                  <CardTitle>Profile Information</CardTitle>
                  <CardDescription>Update how others see you on RunoBlog.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="full_name">Full Name</Label>
                      <Input
                        id="full_name"
                        value={profile.full_name}
                        onChange={(e) => setProfile((p) => ({ ...p, full_name: e.target.value }))}
                        placeholder="Jane Doe"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="username">Username (optional)</Label>
                      <Input
                        id="username"
                        value={profile.username}
                        onChange={(e) => setProfile((p) => ({ ...p, username: e.target.value }))}
                        placeholder="janedoe"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email Address</Label>
                      <Input value={user.email || ""} disabled />
                    </div>
                    <div className="space-y-2">
                      <Label>Account Created</Label>
                      <Input value={createdAt} disabled />
                    </div>
                  </div>
                  <Separator />
                  <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                      Membership:{" "}
                      <span className="font-medium text-foreground">{membership}</span>
                      {membership === "Free" && (
                        <Button
                          variant="link"
                          size="sm"
                          className="px-2"
                          onClick={() => navigate("/pricing")}
                        >
                          Upgrade →
                        </Button>
                      )}
                    </div>
                    <Button onClick={handleSaveProfile} disabled={savingProfile}>
                      {savingProfile ? "Saving…" : "Save Changes"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Security */}
            <TabsContent value="security" className="space-y-6">
              {/* Last sign-in */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <LogIn className="h-5 w-5 text-primary" />
                    Last Login Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">Most recent sign-in</p>
                  <p className="font-medium text-lg">{lastSignIn}</p>
                </CardContent>
              </Card>

              {isEmailUser && (
                <>
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <KeyRound className="h-5 w-5 text-primary" />
                        Change Password
                      </CardTitle>
                      <CardDescription>
                        Use at least 8 characters with a mix of letters, numbers, and symbols.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="current_password">Current password</Label>
                        <div className="relative">
                          <Input
                            id="current_password"
                            type={showCurrent ? "text" : "password"}
                            value={currentPassword}
                            onChange={(e) => {
                              setCurrentPassword(e.target.value);
                              setPwdError(null);
                              setPwdSuccess(false);
                            }}
                            autoComplete="current-password"
                            className="pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowCurrent((v) => !v)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            aria-label={showCurrent ? "Hide password" : "Show password"}
                          >
                            {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="new_password">New password</Label>
                          <div className="relative">
                            <Input
                              id="new_password"
                              type={showNew ? "text" : "password"}
                              value={newPassword}
                              onChange={(e) => {
                                setNewPassword(e.target.value);
                                setPwdError(null);
                                setPwdSuccess(false);
                              }}
                              autoComplete="new-password"
                              className="pr-10"
                            />
                            <button
                              type="button"
                              onClick={() => setShowNew((v) => !v)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              aria-label={showNew ? "Hide password" : "Show password"}
                            >
                              {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="confirm_password">Confirm new password</Label>
                          <div className="relative">
                            <Input
                              id="confirm_password"
                              type={showConfirm ? "text" : "password"}
                              value={confirmPassword}
                              onChange={(e) => {
                                setConfirmPassword(e.target.value);
                                setPwdError(null);
                                setPwdSuccess(false);
                              }}
                              autoComplete="new-password"
                              className="pr-10"
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirm((v) => !v)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              aria-label={showConfirm ? "Hide password" : "Show password"}
                            >
                              {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                          {confirmPassword && confirmPassword !== newPassword && (
                            <p className="text-xs text-destructive">Passwords don't match.</p>
                          )}
                        </div>
                      </div>

                      {newPassword && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">Password strength</span>
                            <span className="font-medium">{strengthLabel}</span>
                          </div>
                          <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                            <div
                              className={`h-full transition-all ${strengthColor}`}
                              style={{ width: `${(strengthScore / 5) * 100}%` }}
                            />
                          </div>
                          <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                            {[
                              ["length", "8+ characters"],
                              ["upper", "Uppercase letter"],
                              ["lower", "Lowercase letter"],
                              ["number", "Number"],
                              ["symbol", "Symbol"],
                            ].map(([key, label]) => {
                              const ok = passwordChecks[key as keyof typeof passwordChecks];
                              return (
                                <li
                                  key={key}
                                  className={`flex items-center gap-1.5 ${ok ? "text-emerald-600 dark:text-emerald-400" : ""}`}
                                >
                                  <CheckCircle2
                                    className={`h-3 w-3 ${ok ? "opacity-100" : "opacity-30"}`}
                                  />
                                  {label}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}

                      {pwdError && (
                        <div
                          role="alert"
                          className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
                        >
                          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <span>{pwdError}</span>
                        </div>
                      )}

                      {pwdSuccess && (
                        <div
                          role="status"
                          className="flex items-start gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-700 dark:text-emerald-400"
                        >
                          <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <span>Password updated successfully.</span>
                        </div>
                      )}

                      <Button
                        onClick={handleChangePassword}
                        disabled={
                          savingPassword ||
                          !currentPassword ||
                          !newPassword ||
                          !confirmPassword
                        }
                      >
                        {savingPassword ? "Updating…" : "Update Password"}
                      </Button>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Reset Password via Email</CardTitle>
                      <CardDescription>
                        We'll email a secure link to {user.email}.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button variant="outline" onClick={handleSendReset} disabled={sendingReset}>
                        {sendingReset ? "Sending…" : "Send Reset Email"}
                      </Button>
                    </CardContent>
                  </Card>
                </>
              )}

              {isGoogle && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                      Connected Google Account
                    </CardTitle>
                    <CardDescription>You signed in using Google.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-3 p-4 rounded-lg border bg-muted/30">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={profile.avatar_url || undefined} />
                        <AvatarFallback>{initials}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{user.email}</p>
                        <p className="text-xs text-muted-foreground">Google · Connected</p>
                      </div>
                      <Badge variant="secondary">Active</Badge>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => window.open("https://myaccount.google.com/permissions", "_blank")}
                    >
                      Manage Connected Account
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Account;
